import type { Clip, Project } from '@/domain/types';
import { insertClips, removeClips, withTrack } from '@/domain/mutate';
import { clipEndTicks } from '@/domain/selectors';
import { runCommand } from './registry';
import { clipsOnLane, maxTakeLane, sliceClip, subtractRange } from '@/domain/slice';
import { joinNearest, planCrossfade } from '@/domain/crossfade';
import { MAX_TAKE_NAME, nextTakeName, setTakeName, takeNamesAfterRemoval } from '@/domain/takes';
import { newClipId } from '@/util/id';
import { defineCommand } from './registry';
import { boolParam, idParam, intParam, stringParam } from './params';

/**
 * Take lanes and comping.
 *
 * Recording a section several times leaves each pass on its own lane; comping
 * is choosing which pass is heard where. Because takes are ordinary clips with
 * a lane index, comping is expressed entirely as slicing and moving clips —
 * the operations the editor already has — rather than as a parallel mechanism
 * with its own rules about saving, undo and export.
 *
 * The comp lane (lane 0) is the only one that sounds. Promoting a range
 * replaces whatever the comp held there with the same range of a chosen take,
 * leaving both the take and the material it displaced intact on their lanes.
 */

const MAX_TAKE_LANES = 64;

export const showTakeLanes = defineCommand<{ trackId: string; show: boolean }>({
  type: 'take.setVisible',
  label: (p) => (p.show ? 'Show take lanes' : 'Hide take lanes'),
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const show = boolParam(raw.show);
    return trackId && show !== null ? { trackId, show } : null;
  },
  apply: (project, p) => withTrack(project, p.trackId, (track) => ({ ...track, showTakeLanes: p.show })),
});

export const promoteTake = defineCommand<{
  trackId: string;
  takeLane: number;
  startTicks: number;
  endTicks: number;
}>({
  type: 'take.promote',
  label: (p) => `Comp take ${p.takeLane}`,
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const takeLane = intParam(raw.takeLane, 1, MAX_TAKE_LANES);
    const startTicks = intParam(raw.startTicks, 0, Number.MAX_SAFE_INTEGER);
    const endTicks = intParam(raw.endTicks, 0, Number.MAX_SAFE_INTEGER);
    if (!trackId || takeLane === null || startTicks === null || endTicks === null) return null;
    if (endTicks <= startTicks) return null;
    return { trackId, takeLane, startTicks, endTicks };
  },
  apply: (project, p) => {
    const track = project.tracks[p.trackId];
    if (!track) return project;

    const source = clipsOnLane(project, p.trackId, p.takeLane);
    if (source.length === 0) return project;

    // Everything the promoted range will take over, cut out of the comp lane
    // first. A clip covered entirely disappears; one covered in the middle
    // leaves two pieces.
    let next = project;
    const displaced = clipsOnLane(project, p.trackId, 0).filter(
      (clip) => clip.startTicks < p.endTicks && clipEndTicks(project, clip) > p.startTicks,
    );

    if (displaced.length > 0) {
      const replacements: Clip[] = [];
      for (const clip of displaced) {
        replacements.push(...subtractRange(project, clip, p.startTicks, p.endTicks, [newClipId(), newClipId()]));
      }
      next = removeClips(next, displaced.map((clip) => clip.id));
      next = insertClips(next, replacements);
    }

    // Then the chosen take's material for that range, copied onto the comp lane.
    const promoted: Clip[] = [];
    for (const clip of source) {
      const slice = sliceClip(project, clip, p.startTicks, p.endTicks, newClipId());
      if (slice) promoted.push({ ...slice, takeLane: 0 });
    }
    if (promoted.length === 0) return project;
    next = insertClips(next, promoted);

    // Comp edits are butt joins by construction, and a butt join between two
    // performances of the same part is exactly where a click lives. A short
    // crossfade at each boundary is what makes a comp sound like a take rather
    // than like an edit; the take material either side supplies the overlap.
    return crossfadeCompBoundaries(next, p.trackId, [p.startTicks, p.endTicks]);
  },
});

/** Softens each boundary of a freshly comped range, where the source allows. */
function crossfadeCompBoundaries(project: Project, trackId: string, ticks: readonly number[]): Project {
  const length = project.transport.crossfadeSec;
  if (length <= 0) return project;

  let next = project;
  for (const tick of ticks) {
    const join = joinNearest(next, trackId, tick, 0);
    // Only the join at this exact edit, not whatever else is nearby.
    if (!join || Math.abs(join.incoming.startTicks - tick) > 1) continue;

    const result = planCrossfade(next, join.outgoing, join.incoming, length);
    if (!result.ok) continue;
    next = {
      ...next,
      clips: {
        ...next.clips,
        [result.plan.outgoing.id]: result.plan.outgoing,
        [result.plan.incoming.id]: result.plan.incoming,
      },
    };
  }
  return next;
}

export const removeTakeLane = defineCommand<{ trackId: string; takeLane: number }>({
  type: 'take.removeLane',
  label: (p) => `Delete take ${p.takeLane}`,
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const takeLane = intParam(raw.takeLane, 1, MAX_TAKE_LANES);
    return trackId && takeLane !== null ? { trackId, takeLane } : null;
  },
  apply: (project, p) => {
    const doomed = clipsOnLane(project, p.trackId, p.takeLane).map((clip) => clip.id);
    if (doomed.length === 0) return project;

    // Lanes above the deleted one slide down, so the numbering stays contiguous
    // and lane 3 does not sit under an empty lane 2.
    const next = removeClips(project, doomed);
    const clips = { ...next.clips };
    for (const clip of Object.values(clips)) {
      if (clip.trackId === p.trackId && clip.takeLane > p.takeLane) {
        clips[clip.id] = { ...clip, takeLane: clip.takeLane - 1 };
      }
    }

    // The names slide with them. A take that was called "the good one" is still
    // the good one after the lane below it is thrown away.
    return withTrack({ ...next, clips }, p.trackId, (track) => ({
      ...track,
      takeNames: takeNamesAfterRemoval(track.takeNames, p.takeLane),
    }));
  },
});

/**
 * Names a take.
 *
 * An empty name is not an error: it clears the name and the lane goes back to
 * being called after its number, which is the only way back once a name has
 * been given.
 */
export const renameTake = defineCommand<{ trackId: string; takeLane: number; name: string }>({
  type: 'take.rename',
  label: (p) => (p.name ? `Rename take to "${p.name}"` : 'Clear take name'),
  parse: (raw) => {
    const trackId = idParam(raw.trackId);
    const takeLane = intParam(raw.takeLane, 1, MAX_TAKE_LANES);
    const name = stringParam(raw.name, MAX_TAKE_NAME, '');
    if (!trackId || takeLane === null || name === null) return null;
    return { trackId, takeLane, name };
  },
  // Typing a name is one edit to undo, not one per keystroke.
  coalesceKey: (p) => `takeName:${p.trackId}:${p.takeLane}`,
  apply: (project, p) =>
    withTrack(project, p.trackId, (track) => {
      const takeNames = setTakeName(track.takeNames, p.takeLane, p.name);
      return { ...track, takeNames };
    }),
});

/**
 * Moves a clip onto a take lane, or back to the comp.
 *
 * This is what makes an existing recording into an alternate: dropping a clip
 * onto a take lane silences it without deleting it.
 */
export const setClipTakeLane = defineCommand<{ clipIds: string[]; takeLane: number }>({
  type: 'take.setClipLane',
  label: (p) => (p.takeLane === 0 ? 'Move to comp' : `Move to take ${p.takeLane}`),
  parse: (raw) => {
    const takeLane = intParam(raw.takeLane, 0, MAX_TAKE_LANES);
    if (!Array.isArray(raw.clipIds) || takeLane === null) return null;
    const clipIds = raw.clipIds.map(idParam);
    if (clipIds.some((id) => id === null) || clipIds.length === 0) return null;
    return { clipIds: clipIds as string[], takeLane };
  },
  apply: (project, p) => {
    const clips = { ...project.clips };
    let changed = false;
    for (const id of p.clipIds) {
      const clip = clips[id];
      if (!clip || clip.takeLane === p.takeLane) continue;
      clips[id] = { ...clip, takeLane: p.takeLane };
      changed = true;
    }
    if (!changed) return project;

    // Showing the lanes when a clip is sent to one avoids the clip appearing to
    // vanish.
    const withClips: Project = { ...project, clips };
    return p.takeLane > 0
      ? withTrack(withClips, clips[p.clipIds[0]]?.trackId ?? '', (track) => ({ ...track, showTakeLanes: true }))
      : withClips;
  },
});

/** The lane a new take should be recorded onto. */
export function nextTakeLane(project: Project, trackId: string): number {
  return Math.min(MAX_TAKE_LANES, maxTakeLane(project, trackId) + 1);
}

/**
 * Files a freshly recorded clip as a take, names it, then comps it in over its
 * own span.
 *
 * Every pass is kept. Recording over a section a second time therefore stacks
 * rather than overwrites: the previous pass stays on its lane, the new one is
 * heard, and either can be comped back in later. That is the whole point of
 * take lanes — a performance you played once should not be destroyed by playing
 * it again.
 *
 * The name is pinned at the moment of recording rather than derived later, so
 * deleting an earlier take renumbers the lanes without renaming the pass.
 */
export function archiveAsTake(project: Project, trackId: string, clipId: string): Project {
  const clip = project.clips[clipId];
  if (!clip) return project;

  const startTicks = clip.startTicks;
  const endTicks = Math.max(startTicks + 1, Math.round(clipEndTicks(project, clip)));
  const lane = nextTakeLane(project, trackId);
  const name = nextTakeName(project, trackId);

  let next = runCommand(project, { type: 'take.setClipLane', params: { clipIds: [clipId], takeLane: lane } }).project;
  next = runCommand(next, { type: 'take.rename', params: { trackId, takeLane: lane, name } }).project;
  next = runCommand(next, { type: 'take.promote', params: { trackId, takeLane: lane, startTicks, endTicks } }).project;
  return next;
}
